<script setup lang="ts">
import { CheckCircleIcon, ExclamationCircleIcon, IdentificationIcon } from '@heroicons/vue/solid'

import { computed, ref } from 'vue'
import { i18n } from '../../../localization'
import Alert from '../../base/Alert.vue'
import { LegalAccountData, useLegalAccountDataState } from '../../../utilities/account/useAccountDataLegal'
import {
    LegalAccountNavigationStore,
    navigateTo,
    useLegalAccountNavigationState,
} from '../../../utilities/account/useAccountNavigationLegal'
import AccountPanelLegalVerification from './pages/AccountPanelLegalVerification.vue'

const state = useLegalAccountDataState()

const navigationState = useLegalAccountNavigationState() as LegalAccountNavigationStore

const isVerified = computed(() => state.legalProfile.kyc_legal)

const titleText = computed(() => {
    if (isVerified.value) {
        return i18n.global.t('verification.verification_successful')
    } else {
        return i18n.global.t('verification.kyc_process')
    }
})

const descriptionText = computed(() => {
    if (isVerified.value) {
        return i18n.global.t('verification.verification_successful_description')
    } else {
        return i18n.global.t('verification.kyc_process_description')
    }
})

const props = withDefaults(
    defineProps<{
        onButtonClick: () => void
        quickEdit?: boolean
    }>(),
    {
        onButtonClick: () => {
            navigateTo('Verification')
        },
    }
)

function clickButton() {
    if (props.quickEdit) {
        showQuickEdit.value = !showQuickEdit.value
    } else {
        props.onButtonClick()
    }
}

const showQuickEdit = ref(false)

const buttonText = computed(() => {
    if (props.quickEdit && showQuickEdit.value) {
        return i18n.global.t('dialog.button.cancel')
    }
    return i18n.global.t('verification.kyc_process_action_button')
})
</script>

<template>
    <div :class="{ 'rounded-md border': quickEdit }">
        <Alert
            :class="{ 'border-none shadow-none': quickEdit }"
            :title-text="titleText"
            :description-text="descriptionText"
            :show-button="(!isVerified && navigationState.page !== 'Verification') || quickEdit"
            :button-text="buttonText"
            @click-button="clickButton">
            <template #icon>
                <IdentificationIcon class="h-12 w-12 text-gray-300"></IdentificationIcon>
                <ExclamationCircleIcon
                    v-if="!isVerified"
                    class="absolute bottom-0 right-0 h-6 w-6 rounded-full bg-white text-red-400"></ExclamationCircleIcon>
                <CheckCircleIcon
                    v-else
                    class="absolute bottom-0 right-0 h-6 w-6 rounded-full bg-white text-green-500"></CheckCircleIcon>
            </template>
        </Alert>
        <div v-if="showQuickEdit" class="border-t">
            <AccountPanelLegalVerification />
        </div>
    </div>
</template>

<style scoped></style>
